
















import { defineComponent, onMounted, reactive } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
const { useState, useActions } = createNamespacedHelpers('navigationStore');
import { ResponsiveUtils } from '@olbbank/vue-components';
import Vue from 'vue';

type VueRootInstance = {
	showWUWLoginOverlay?: boolean;
} & Vue;
/**
 * Reusable navigation component.
 */
export default defineComponent({
	setup(_, { root }) {
		const responsiveUtils = new ResponsiveUtils();
		const { isMenuActive } = useState(['isMenuActive']);
		const { updateMenuState, setActive1stLayer, setActive2ndLayer } = useActions([
			'updateMenuState',
			'setActive1stLayer',
			'setActive2ndLayer',
		]);

		const state = reactive({
			showBackdrop: false as boolean,
			currentWindowWidth: 0,
		});

		function setActiveLayer(id: undefined | number) {
			setActive2ndLayer({
				active2ndLayer: id,
			});
		}

		// always start with a closed navigation flyout
		setActiveLayer(undefined);

		function setShowBackdrop(show: boolean) {
			state.showBackdrop = show;
			// there's never a layer active, when the backdrop is hidden
			if (!show) {
				setActiveLayer(undefined);
			}
		}

		function setDefaultFirstLayer() {
			// we have to use a timeout, otherwise accessing a specific ref during mounting would return undefined.
			// (Vue.nextTick isn't enough unfortunately)
			setTimeout(() => {
				if (root.$refs.navFirstLayer) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
					//@ts-ignore
					const active1stLayer = root.$refs.navFirstLayer.state.selected.text;
					setActive1stLayer({
						active1stLayer: active1stLayer,
					});
				}
			});
		}

		// toggle the menu state on viewport change
		function updateActiveMenuByVP() {
			// Permanently show the menu on large screens
			if (responsiveUtils.isLgUp()) {
				// set menu permanently to active
				updateMenuState(true);
				// we can only set the first layer for the large viewports,
				// otherwise the nav first layer select is undefined
				// because the nav is hidden in small viewports by default
				setDefaultFirstLayer();
			} else {
				// close the menu and also the backdrop
				updateMenuState(false);
				setShowBackdrop(false);
			}
		}

		function resizeHandler() {
			// ONLY update the menu state, if the WIDTH resizes, not the height
			// this is necessary because in iOS Safari's top navigation bar collapses on scroll
			// and therefore would close the menu unwantedly!
			if (state.currentWindowWidth !== window.innerWidth) {
				state.currentWindowWidth = window.innerWidth;
				updateActiveMenuByVP();
			}
		}
		// initialize VP width
		state.currentWindowWidth = window.innerWidth;
		window.addEventListener('resize', resizeHandler);
		onMounted(() => {
			updateActiveMenuByVP();
		});

		// toggle the menu state on purpose
		function toggleActiveMenu() {
			updateMenuState(!isMenuActive.value);
			// when closing the mobile nav:
			// also make sure that quicknav and flyout are closed
			if (!isMenuActive.value) {
				setShowBackdrop(false);
				// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
				//@ts-ignore
				root.$refs.quicknav?.setActiveQuickFlyout(undefined);
			} else {
				// only relevant for WUW: hide the overlay where
				// depot + onlinebanking login are placed when opening the menu on mobile devices
				if ((root as VueRootInstance).showWUWLoginOverlay) {
					(root as VueRootInstance).showWUWLoginOverlay = false;
				}
				// only for small VP: we have to set the first layer here (in case none is set by the data json),
				// because otherwise the first layer's ref would be undefined.
				setDefaultFirstLayer();
			}
		}

		function openFlyout(event: Event, activeLayer: number) {
			event.preventDefault();
			setShowBackdrop(true);
			setActiveLayer(activeLayer);
			if ((root as VueRootInstance).showWUWLoginOverlay) {
				(root as VueRootInstance).showWUWLoginOverlay = false;
			}
		}
		return { state, setActiveLayer, setShowBackdrop, toggleActiveMenu, openFlyout, updateActiveMenuByVP };
	},
});
